import { getFromStorage } from './storage';

/**
 * AVO Staking & Validator RPC utilities
 * Integrates with the staking and validator management system
 */

const STAKING_RPC_URL = 'http://localhost:9545';

/**
 * Makes RPC call to AVO node for staking operations
 */
const makeStakingRpcCall = async (method, params = []) => {
  try {
    const response = await fetch(STAKING_RPC_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        jsonrpc: '2.0',
        method,
        params,
        id: Date.now()
      })
    });

    const data = await response.json();
    
    if (data.error) {
      throw new Error(data.error.message || 'Staking RPC Error');
    }
    
    return data.result;
  } catch (err) {
    console.error(`Staking RPC Error [${method}]:`, err);
    throw new Error(`Failed to call ${method}: ${err.message}`);
  }
};

/**
 * Stake AVO tokens to become a validator or delegate to existing validator
 * @param {string} staker - Staker address
 * @param {string} amount - Amount to stake in AVO
 * @param {string} validator - Validator address (optional, for delegation)
 * @returns {Promise<Object>} - Staking transaction result
 */
export const stakeAvo = async (staker, amount, validator = null) => {
  try {
    const params = [{
      staker,
      amount,
      validator_address: validator
    }];

    const result = await makeStakingRpcCall('avo_stake', params);
    return result;
  } catch (err) {
    console.error('Error staking AVO:', err);
    throw err;
  }
};

/**
 * Unstake AVO tokens
 * @param {string} staker - Staker address
 * @param {string} amount - Amount to unstake
 * @returns {Promise<Object>} - Unstaking transaction result
 */
export const unstakeAvo = async (staker, amount) => {
  try {
    const params = [{
      staker,
      amount
    }];

    const result = await makeStakingRpcCall('avo_unstake', params);
    return result;
  } catch (err) {
    console.error('Error unstaking AVO:', err);
    throw err;
  }
};

/**
 * Get staking information for an address
 * @param {string} address - Address to query
 * @returns {Promise<Object>} - Staking information
 */
export const getStakingInfo = async (address) => {
  try {
    const params = [{
      address
    }];

    const result = await makeStakingRpcCall('avo_getStakingInfo', params);
    return result || {
      total_staked: '0',
      active_stake: '0',
      pending_rewards: '0',
      validator_status: 'none',
      delegations: [],
      unstaking_requests: []
    };
  } catch (err) {
    console.error('Error getting staking info:', err);
    // Return default structure if RPC not available
    return {
      total_staked: '0',
      active_stake: '0',
      pending_rewards: '0',
      validator_status: 'none',
      delegations: [],
      unstaking_requests: []
    };
  }
};

/**
 * Get list of all validators
 * @returns {Promise<Array>} - List of validators
 */
export const getValidators = async () => {
  try {
    const result = await makeStakingRpcCall('avo_getValidators', []);
    return result || [];
  } catch (err) {
    console.error('Error getting validators:', err);
    // Return mock data for development
    return [
      {
        address: '0x372d3c99b7bdb6dec219daf4aef96bc2062e6090',
        name: 'Genesis Validator',
        total_stake: '10000000.0',
        commission: '5.0',
        uptime: '99.8',
        status: 'active',
        delegators: 42,
        last_block: Date.now() - 5000
      },
      {
        address: '0x1234567890123456789012345678901234567890',
        name: 'Community Validator #1',
        total_stake: '5000000.0',
        commission: '3.0',
        uptime: '99.2',
        status: 'active',
        delegators: 28,
        last_block: Date.now() - 12000
      },
      {
        address: '0xabcdefabcdefabcdefabcdefabcdefabcdefabcd',
        name: 'DeFi Validator',
        total_stake: '3500000.0',
        commission: '7.0',
        uptime: '98.9',
        status: 'active',
        delegators: 15,
        last_block: Date.now() - 8000
      }
    ];
  }
};

/**
 * Claim staking rewards
 * @param {string} staker - Staker address
 * @returns {Promise<Object>} - Claim transaction result
 */
export const claimRewards = async (staker) => {
  try {
    const params = [{
      staker
    }];

    const result = await makeStakingRpcCall('avo_claimRewards', params);
    return result;
  } catch (err) {
    console.error('Error claiming rewards:', err);
    throw err;
  }
};

/**
 * Register as a validator
 * @param {string} validator - Validator address
 * @param {string} name - Validator name
 * @param {string} commission - Commission rate (0-100)
 * @param {string} minStake - Minimum stake amount
 * @returns {Promise<Object>} - Registration result
 */
export const registerValidator = async (validator, name, commission, minStake) => {
  try {
    const params = [{
      validator,
      name,
      commission,
      min_stake: minStake
    }];

    const result = await makeStakingRpcCall('avo_registerValidator', params);
    return result;
  } catch (err) {
    console.error('Error registering validator:', err);
    throw err;
  }
};

/**
 * Get staking statistics
 * @returns {Promise<Object>} - Staking statistics
 */
export const getStakingStats = async () => {
  try {
    const result = await makeStakingRpcCall('avo_getStakingStats', []);
    return result || {
      total_staked: '18500000.0',
      total_validators: 3,
      active_validators: 3,
      average_uptime: 99.3,
      total_rewards_distributed: '250000.0',
      staking_apr: '12.5'
    };
  } catch (err) {
    console.error('Error getting staking stats:', err);
    // Return mock data for development
    return {
      total_staked: '18500000.0',
      total_validators: 3,
      active_validators: 3,
      average_uptime: 99.3,
      total_rewards_distributed: '250000.0',
      staking_apr: '12.5'
    };
  }
};

/**
 * Format staking amount for display
 * @param {string} amount - Amount in wei or base units
 * @returns {string} - Formatted amount
 */
export const formatStakingAmount = (amount) => {
  if (!amount) return '0.0000';
  const numAmount = parseFloat(amount);
  if (numAmount >= 1000000) {
    return `${(numAmount / 1000000).toFixed(2)}M`;
  } else if (numAmount >= 1000) {
    return `${(numAmount / 1000).toFixed(2)}K`;
  }
  return numAmount.toFixed(4);
};

/**
 * Calculate staking rewards
 * @param {string} stakedAmount - Amount staked
 * @param {string} apr - Annual percentage rate
 * @param {number} days - Number of days
 * @returns {string} - Estimated rewards
 */
export const calculateStakingRewards = (stakedAmount, apr, days = 365) => {
  if (!stakedAmount || !apr) return '0';
  const amount = parseFloat(stakedAmount);
  const rate = parseFloat(apr) / 100;
  const rewards = (amount * rate * days) / 365;
  return rewards.toFixed(6);
};

/**
 * Get validator status information
 * @param {string} status - Validator status
 * @returns {Object} - Status info with icon and color
 */
export const getValidatorStatusInfo = (status) => {
  const statusMap = {
    active: { label: 'Active', icon: '✅', color: '#4caf50' },
    inactive: { label: 'Inactive', icon: '⏸️', color: '#ff9800' },
    jailed: { label: 'Jailed', icon: '🔒', color: '#f44336' },
    pending: { label: 'Pending', icon: '⏳', color: '#2196f3' },
    none: { label: 'Not Validator', icon: '👤', color: '#9e9e9e' }
  };
  return statusMap[status] || statusMap.none;
};

/**
 * Get staking status for display
 * @param {Object} stakingInfo - Staking information
 * @returns {Object} - Status info
 */
export const getStakingStatus = (stakingInfo) => {
  if (!stakingInfo) {
    return { 
      status: 'Not Staking', 
      icon: '💤', 
      color: '#9e9e9e',
      description: 'Start staking to earn rewards'
    };
  }

  const totalStaked = parseFloat(stakingInfo.total_staked || '0');
  
  if (totalStaked === 0) {
    return { 
      status: 'Not Staking', 
      icon: '💤', 
      color: '#9e9e9e',
      description: 'Start staking to earn rewards'
    };
  }

  if (stakingInfo.validator_status !== 'none') {
    return { 
      status: 'Validator', 
      icon: '👑', 
      color: '#9c27b0',
      description: 'Operating as validator node'
    };
  }

  return { 
    status: 'Delegating', 
    icon: '🤝', 
    color: '#2196f3',
    description: 'Delegated to validator(s)'
  };
};

/**
 * Validate staking amount
 * @param {string} amount - Amount to validate
 * @param {string} balance - Available balance
 * @param {string} minStake - Minimum stake requirement
 * @returns {Object} - Validation result
 */
export const validateStakingAmount = (amount, balance, minStake = '100') => {
  const numAmount = parseFloat(amount || '0');
  const numBalance = parseFloat(balance || '0');
  const numMinStake = parseFloat(minStake);

  if (numAmount <= 0) {
    return { valid: false, error: 'Amount must be greater than 0' };
  }

  if (numAmount < numMinStake) {
    return { valid: false, error: `Minimum stake is ${minStake} AVO` };
  }

  if (numAmount > numBalance) {
    return { valid: false, error: 'Insufficient balance' };
  }

  return { valid: true };
};

/**
 * Get minimum stake requirement for validators
 * @returns {string} - Minimum stake amount
 */
export const getMinimumValidatorStake = () => {
  return '1000000'; // 1M AVO minimum for validators
};

/**
 * Get minimum delegation amount
 * @returns {string} - Minimum delegation amount
 */
export const getMinimumDelegation = () => {
  return '100'; // 100 AVO minimum for delegation
};

/**
 * Calculate validator commission
 * @param {string} rewards - Reward amount
 * @param {string} commission - Commission rate (0-100)
 * @returns {Object} - Commission breakdown
 */
export const calculateValidatorCommission = (rewards, commission) => {
  const rewardAmount = parseFloat(rewards || '0');
  const commissionRate = parseFloat(commission || '0') / 100;
  const commissionAmount = rewardAmount * commissionRate;
  const delegatorRewards = rewardAmount - commissionAmount;

  return {
    total_rewards: rewardAmount.toFixed(6),
    commission_amount: commissionAmount.toFixed(6),
    delegator_rewards: delegatorRewards.toFixed(6),
    commission_rate: commission
  };
};

export default {
  stakeAvo,
  unstakeAvo,
  getStakingInfo,
  getValidators,
  claimRewards,
  registerValidator,
  getStakingStats,
  formatStakingAmount,
  calculateStakingRewards,
  getValidatorStatusInfo,
  getStakingStatus,
  validateStakingAmount,
  getMinimumValidatorStake,
  getMinimumDelegation,
  calculateValidatorCommission
};